"use client"

import Image from "next/image"
import { useTranslations } from "@/lib/intl/use-translations"
import type { ReviewerExpert } from "@/lib/reviewer-experts"
import { blobMediaUrl } from "@/lib/blob-url"
import { BodyFigureIcon, ImpactAreaIcon } from "@/components/ui/icons"
import { Sparkle } from "@phosphor-icons/react"
import { useCountry } from "@/components/layout/country-provider"

/**
 * Credibility logos — affiliations / standards shown beneath the supporting
 * proof. University marks mirror the experts data; Siemens Healthineers is the
 * scanner platform; CE marking is the EU conformity sign.
 */
const CREDIBILITY_LOGOS: { src: string; alt: string }[] = [
  { src: "/images/logos/siemens.svg", alt: "Siemens Healthineers" },
  { src: "/images/logos/karolinska.svg", alt: "Karolinska Institutet" },
  { src: "/images/logos/st-gallen.svg", alt: "University of St.Gallen" },
]

/** Advisory profiles shown in the stat block, in this fixed order. */
const ADVISORY_ORDER = ["Donati", "Fischer", "Battegay"]

/** "Expertise" — stat-anchored credibility section. */
export function MriCredibility({
  experts = [],
}: {
  experts?: ReviewerExpert[]
}) {
  const tt = useTranslations("tests") as unknown as (k: string) => string
  // AT hides the Swiss experts module on every page (DoctorsSection, the
  // test-page block), so the Zurich advisory cards drop out here too. The
  // "30,000+ MRTs" stat goes with them (AHD-13698): it is the Swiss
  // radiologists' track record. The supporting proof below stays.
  // country-gate-ignore: AT content hide, not a page gate
  const hideAdvisory = useCountry().code === "at"
  const hideStat = hideAdvisory

  // Pin the advisory profiles (Donati, Fischer, Battegay) in order. If a
  // pinned profile isn't in the CMS set yet (e.g. still a draft), fill the
  // remaining slots with the next available experts so three always render.
  const pinned = ADVISORY_ORDER.map((s) =>
    experts.find((e) => e.name.includes(s))
  ).filter((e): e is ReviewerExpert => Boolean(e))
  const advisoryExperts = [
    ...pinned,
    ...experts.filter((e) => !pinned.includes(e)),
  ].slice(0, 3)

  const supportingItems = [
    {
      keyBase: "mriCred3",
      icon: <BodyFigureIcon size={28} className="text-neutral-900" />,
    },
    {
      keyBase: "mriCred4",
      // The CE conformity mark itself (the standard's own logo) instead of a
      // generic icon. Height-constrained to match the 28px icon row.
      icon: (
        <Image
          src={blobMediaUrl("/images/logos/ce-marking.svg")}
          alt=""
          width={34}
          height={24}
          className="h-6 w-auto"
        />
      ),
    },
    {
      keyBase: "mriCred5",
      // Base icon with a Phosphor sparkle overlapping its right edge,
      // signalling the "AI-assisted / expanding" angle.
      icon: (
        <span className="relative inline-block">
          <ImpactAreaIcon size={28} className="text-neutral-900" />
          <Sparkle
            size={20}
            weight="fill"
            aria-hidden="true"
            className="absolute top-1/2 -right-3 -translate-y-1/2 text-neutral-900"
          />
        </span>
      ),
    },
  ]

  return (
    <section
      id="mri-credibility"
      className="bg-white"
      data-testid="credibility-card"
    >
      <div className="mx-auto max-w-[1100px] px-4 pt-6 pb-16 md:px-10 md:pt-8 md:pb-20">
        <div className="text-center">
          <p className="type-label-lg text-neutral-500 uppercase">
            {tt("mriCredLabel")}
          </p>
          {/* Hero stat — anchors the section. */}
          {!hideStat && (
            <>
              <p className="type-display-lg mt-6 text-neutral-900">30,000+</p>
              <p className="type-body-sm mt-2 text-neutral-500">
                {tt("mriCredStatNote")}
              </p>
            </>
          )}
          {/* Advisory radiologists — hover reveals each bio. */}
          {!hideAdvisory && advisoryExperts.length > 0 && (
            <div className="mx-auto mt-10 grid max-w-[360px] grid-cols-1 gap-4 sm:max-w-[860px] sm:grid-cols-3 sm:gap-4">
              {advisoryExperts.map((doctor) => (
                <div
                  key={doctor.name}
                  className="group relative aspect-[326/397] overflow-hidden rounded-[12px]"
                >
                  <Image
                    src={doctor.image}
                    alt={doctor.name}
                    fill
                    sizes="(min-width: 640px) 280px, 90vw"
                    className="object-cover"
                  />
                  <div className="absolute inset-0 bg-black opacity-0 transition-opacity duration-300 ease-[cubic-bezier(0.16,1,0.3,1)] group-hover:opacity-40" />
                  <div className="absolute right-1.5 bottom-1.5 left-1.5 z-10 flex flex-col gap-1 text-left">
                    <div className="rounded-[8px] bg-white/[0.77] px-2 py-1.5 backdrop-blur-sm">
                      <p className="type-label-md leading-snug font-medium text-neutral-900">
                        {doctor.name}
                      </p>
                    </div>
                    <div className="max-h-0 overflow-hidden opacity-0 transition-[max-height,opacity] duration-300 ease-[cubic-bezier(0.16,1,0.3,1)] group-hover:max-h-56 group-hover:opacity-100">
                      <div className="rounded-[8px] bg-white/[0.24] px-2 py-1.5">
                        <p className="type-body-sm leading-snug text-white">
                          {doctor.title}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Supporting proof — 3-up (no divider lines) */}
        <div className="mt-16 grid grid-cols-1 gap-x-10 gap-y-10 md:grid-cols-3">
          {supportingItems.map(({ keyBase, icon }) => (
            <div key={keyBase}>
              {icon}
              <h3 className="type-title-md mt-4 text-neutral-900">
                {tt(`${keyBase}Title`)}
              </h3>
              <p className="type-body-md mt-3 text-neutral-600">
                {tt(`${keyBase}Body`)}
              </p>
            </div>
          ))}
        </div>

        {/* Affiliation / standards logo strip (no separators) */}
        <div className="mt-12 flex flex-wrap items-center justify-center gap-x-10 gap-y-5">
          {CREDIBILITY_LOGOS.map((logo) => (
            <Image
              key={logo.src}
              src={blobMediaUrl(logo.src)}
              alt={logo.alt}
              width={140}
              height={24}
              className="h-6 w-auto opacity-70"
            />
          ))}
        </div>
      </div>
    </section>
  )
}
